import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Users, MapPin, Phone, Mail, Search, Filter, Download, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AdminLogin from './AdminLogin';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('adminLoggedIn') === 'true';
  });
  
  const handleLogin = () => {
    sessionStorage.setItem('adminLoggedIn', 'true');
    setIsAuthenticated(true);
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboardContent />;
};

const AdminDashboardContent = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0
  });

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings?limit=1000`);
      const data = await response.json();
      setBookings(data);
      
      // Calculate stats
      const statsCalc = {
        total: data.length,
        pending: data.filter(b => b.status === 'pending').length,
        confirmed: data.filter(b => b.status === 'confirmed').length,
        completed: data.filter(b => b.status === 'completed').length,
        cancelled: data.filter(b => b.status === 'cancelled').length
      };
      setStats(statsCalc);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchBookings(); // Refresh
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const exportToCSV = () => {
    const csv = [
      ['ID', 'Cliente', 'Email', 'Telefono', 'Da', 'A', 'Data', 'Ora', 'Passeggeri', 'Status', 'Creato'].join(','),
      ...filteredBookings.map(b => [
        b.id,
        b.customer_name,
        b.customer_email,
        b.customer_phone,
        b.from_location,
        b.to_location,
        b.date,
        b.time,
        `${b.adults + b.children + b.infants}`,
        b.status,
        new Date(b.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prenotazioni_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredBookings = bookings
    .filter(b => filter === 'all' || b.status === filter)
    .filter(b => 
      searchTerm === '' || 
      b.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.customer_phone?.includes(searchTerm) ||
      b.from_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.to_location?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'In Attesa';
      case 'confirmed': return 'Confermata';
      case 'completed': return 'Completata';
      case 'cancelled': return 'Cancellata';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-4">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2">Dashboard Amministratore</h1>
            <p className="text-gray-400">Gestisci tutte le prenotazioni</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => window.location.href = '/admin/pricing'}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Configura Prezzi
            </Button>
            <Button
              onClick={() => {
                sessionStorage.removeItem('adminLoggedIn');
                window.location.reload();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Totale</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-amber-200" />
            </div>
          </Card>

          <Card className="p-6 bg-white border-2 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">In Attesa</p>
                <p className="text-3xl font-bold mt-1 text-yellow-600">{stats.pending}</p>
              </div>
              <Calendar className="w-10 h-10 text-yellow-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Confermate</p>
                <p className="text-3xl font-bold mt-1 text-blue-600">{stats.confirmed}</p>
              </div>
              <Users className="w-10 h-10 text-blue-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Completate</p>
                <p className="text-3xl font-bold mt-1 text-green-600">{stats.completed}</p>
              </div>
              <MapPin className="w-10 h-10 text-green-400" />
            </div>
          </Card>

          <Card className="p-6 bg-white border-2 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Cancellate</p>
                <p className="text-3xl font-bold mt-1 text-red-600">{stats.cancelled}</p>
              </div>
              <Users className="w-10 h-10 text-red-400" />
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setFilter('all')}
                className={`${filter === 'all' ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Tutte ({stats.total})
              </Button>
              <Button
                onClick={() => setFilter('pending')}
                className={`${filter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                In Attesa ({stats.pending})
              </Button>
              <Button
                onClick={() => setFilter('confirmed')}
                className={`${filter === 'confirmed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Confermate ({stats.confirmed})
              </Button>
              <Button
                onClick={() => setFilter('completed')}
                className={`${filter === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                Completate ({stats.completed})
              </Button>
            </div>

            <div className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Cerca per nome, telefono, località..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 text-white">
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </Card>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Nessuna prenotazione trovata</p>
            </Card>
          ) : (
            filteredBookings.map((booking) => (
              <Card key={booking.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Left: Booking Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {new Date(booking.created_at).toLocaleDateString('it-IT', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                          {booking.customer_name || 'Nome non fornito'}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          {booking.customer_phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4" />
                              <span>{booking.customer_phone}</span>
                            </div>
                          )}
                          {booking.customer_email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <span>{booking.customer_email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-gray-600">Da: <span className="font-semibold text-gray-900">{booking.from_location}</span></p>
                              <p className="text-sm text-gray-600">A: <span className="font-semibold text-gray-900">{booking.to_location}</span></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-amber-600" />
                            <span className="font-semibold">{booking.date}</span>
                            <span className="text-gray-500">ore</span>
                            <span className="font-semibold">{booking.time}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="w-4 h-4 text-amber-600" />
                            <span>{booking.adults} adulti{booking.children > 0 && `, ${booking.children} bambini`}{booking.infants > 0 && `, ${booking.infants} neonati`}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex lg:flex-col gap-2">
                    {booking.status === 'pending' && (
                      <>
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                          className="bg-blue-600 hover:bg-blue-700 text-white flex-1 lg:w-32"
                        >
                          Conferma
                        </Button>
                        <Button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          className="bg-red-600 hover:bg-red-700 text-white flex-1 lg:w-32"
                        >
                          Cancella
                        </Button>
                      </>
                    )}
                    {booking.status === 'confirmed' && (
                      <Button
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="bg-green-600 hover:bg-green-700 text-white flex-1 lg:w-32"
                      >
                        Completa
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
